/**
 * The AI Employee roster (plans/00 5.1, TDD D9, B20). The only source of
 * names, monograms, hues and role lines in the frontend; mirrored in Settings
 * `ai_employee_roster` for the backend. Monograms only, never personal names
 * or photos.
 */

export type AIEmployeeId =
  | 'situation-analyst'
  | 'report-drafter'
  | 'supply-coordinator'
  | 'prefecture-liaison'
  | 'orchestrator'

/** `--orch-n` connector hue, or `rail` for the orchestrator's navy. */
export type AIEmployeeHue = 1 | 2 | 3 | 4 | 'rail'

export interface AIEmployee {
  id: AIEmployeeId
  name: string
  roleLine: string
  monogram: string
  hue: AIEmployeeHue
  /** Autos (A0 to A6) and voice agents (V1, V2) that work under this AI Employee. */
  members: readonly string[]
}

export const AI_EMPLOYEES: readonly AIEmployee[] = [
  {
    id: 'situation-analyst',
    name: 'Situation Analyst',
    roleLine: 'Shows isolation, impact and gaps within 3 hours',
    monogram: 'SA',
    hue: 1,
    members: ['A1', 'A2', 'V2'],
  },
  {
    id: 'report-drafter',
    name: 'Report Drafter',
    roleLine: 'Drafts report No. N and the situation report from confirmed figures',
    monogram: 'RD',
    hue: 2,
    members: ['A3'],
  },
  {
    id: 'supply-coordinator',
    name: 'Supply Coordinator',
    roleLine: 'Supplies to people needing care and isolated communities within 3 days',
    monogram: 'SC',
    hue: 3,
    members: ['A4', 'A6'],
  },
  {
    id: 'prefecture-liaison',
    name: 'Prefecture Liaison',
    roleLine: 'One request per prefecture; contacts every agency and tracks who acknowledged',
    monogram: 'PL',
    hue: 4,
    members: ['A5', 'V1'],
  },
  {
    id: 'orchestrator',
    name: 'AI Office Orchestrator',
    roleLine: 'Starts the Disaster Management AI Office on POL-1 and runs each HQ cycle. Decides nothing',
    monogram: 'AO',
    hue: 'rail',
    members: ['A0'],
  },
]

/** Autos and voice agents by id, with their screen names (plans/00 5.2). */
export const AUTO_NAMES: Readonly<Record<string, string>> = {
  A0: 'A0 AI Office Orchestrator',
  A1: 'A1 Intake and Resolve',
  A2: 'A2 Situation Analyst',
  A3: 'A3 Report Drafter',
  A4: 'A4 Supply Coordinator',
  A5: 'A5 Prefecture Liaison',
  A6: 'A6 Delivery Monitor',
  V1: 'V1 Agency Notifier',
  V2: 'V2 AI Office Line',
}

/**
 * The AI Employee for an employee id, an Auto id (A0 to A6) or a voice agent
 * id (V1, V2). A1 is shared with the Report Drafter and resolves to its home,
 * the Situation Analyst. Unknown ids give null.
 */
export function resolveAIEmployee(id: string | null | undefined): AIEmployee | null {
  if (!id) return null
  const key = id.trim()
  const direct = AI_EMPLOYEES.find((e) => e.id === key)
  if (direct) return direct
  const upper = key.toUpperCase()
  return AI_EMPLOYEES.find((e) => e.members.includes(upper)) ?? null
}

/** "Situation Analyst · AI Employee". */
export function aiEmployeeLabel(employee: AIEmployee): string {
  return `${employee.name} · AI Employee`
}
